import * as React from 'react'
import { Dispatch, FunctionComponent, ReactElement, SetStateAction, useState } from 'react'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import TimeForm from '../../Form/Time/TimeForm'
import useAxios from '../../../hooks/useAxios'
import { AxiosInstance } from 'axios'
import { TimeMutationDto } from '../../../config/registrationConfig'
import { TimeSuggestionStatus } from '../../../pages/Time/BaseTimeSuggestionOverview'
import CommandDialog, { Dismiss } from '../Command/CommandDialog'
import { ActionButton, DefaultButton, DialogFooter, PrimaryButton } from '@fluentui/react'
import { useTimeDivisorSetting } from '../../../hooks/file/useTimeDivisorSetting'
import { useTranslation } from 'react-i18next'

export type TimeSuggestionMutationDto = {
    status: TimeSuggestionStatus
}

export type TrackTimeSuggestionDto = TimeMutationDto & {
    timeSuggestionId: string
}

type Props = {
    hidden: boolean
    initialValues: Partial<TrackTimeSuggestionDto>
    onDismissAndReload?: () => void
    onDismiss?: () => void
}

type ButtonsProps = {
    dismiss: Dismiss
    dialogProps: Props
    setGlobalError: Dispatch<SetStateAction<NormalizedError>>
}

export function onClickTrackTime(axios: AxiosInstance, props: Props, onError: (error: any) => void) {
    return (time: TrackTimeSuggestionDto) => {
        return axios
            .post(`/time-suggestion/${time.timeSuggestionId}/time`, time, { headers: { SuppressErrors: 'true' } })
            .then(() => {
                props.onDismissAndReload?.()
            })
            .catch(error => onError(error))
    }
}

export function onClickDontTrackTime(axios: AxiosInstance, props: Props, onError: (error: any) => void) {
    return () => {
        const mutation: TimeSuggestionMutationDto = { status: TimeSuggestionStatus.NoTracking }
        axios
            .put(`/time-suggestion/${props.initialValues.timeSuggestionId}`, mutation, {
                headers: { SuppressErrors: 'true' },
            })
            .then(() => {
                props.onDismissAndReload?.()
            })
            .catch(error => onError(error))
    }
}

function processError(
    error: any,
    setGlobalError: (value: ((prevState: NormalizedError) => NormalizedError) | NormalizedError) => void,
    translate: (key: string) => string,
) {
    if (error.response.data.status === 400) {
        setGlobalError({ ...normalizeAxiosError(error), message: translate('time.suggestion.error.duplicate') })
    } else {
        setGlobalError(normalizeAxiosError(error))
    }
}

const TimeSuggestionButtons: FunctionComponent<ButtonsProps> = props => {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    return (
        <DialogFooter>
            <ActionButton text={translate('action.cancel')} onClick={props.dismiss} />
            <DefaultButton
                text={translate('time.suggestion.donttrack')}
                onClick={onClickDontTrackTime(axios, props.dialogProps, (error: any) => processError(error, props.setGlobalError, translate))}
            />
            <PrimaryButton text={translate('time.suggestion.track')} type="submit" />
        </DialogFooter>
    )
}

export default function TimeSuggestionDialog(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [globalError, setGlobalError] = useState<NormalizedError>()
    const timeDivisorSetting = useTimeDivisorSetting()

    return (
        <CommandDialog dialogContentProps={{ title: translate('time.suggestion.title') }} hidden={props.hidden} onDismiss={props.onDismiss}>
            {() => (
                <TimeForm
                    initialValues={{ ...props.initialValues, duration: timeDivisorSetting.initialDuration }}
                    onSubmit={onClickTrackTime(axios, props, (error: any) => processError(error, setGlobalError, translate))}
                    globalError={globalError}
                >
                    <TimeSuggestionButtons dismiss={props.onDismiss} dialogProps={props} setGlobalError={setGlobalError} />
                </TimeForm>
            )}
        </CommandDialog>
    )
}
